import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import {
    ProactiveActivityItem,
    ProactiveActivityService,
    ProactiveItemAction,
    ProactiveItemView,
} from '../../services/proactive-activity.service';
import { ScopeRung } from '../../services/ddr.service';
import { ToastService } from '../../services/toast.service';
import { ScopeChooser } from '../scope-chooser/scope-chooser';
import { environment } from '../../../environments/environment';

type CorrectionKind = 'not_relevant' | 'wrong_tone' | 'inaccurate' | 'other';

const CORRECTION_LABELS: Record<CorrectionKind, string> = {
    not_relevant: 'Not worth replying - auto-dismiss similar',
    wrong_tone: "Doesn't sound like me",
    inaccurate: 'Wrong / inaccurate',
    other: 'Other',
};

/** Pop-out modal for a single triage item - full subject, sender, full
 *  email body, full drafted reply, and oversized action buttons. Opened
 *  from a rail row click; the rail's compact grid stays as the index
 *  and this view is the read surface. */
@Component({
    selector: 'app-email-review-dialog',
    standalone: true,
    imports: [FormsModule, ScopeChooser],
    templateUrl: './email-review-dialog.html',
    styleUrl: './email-review-dialog.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EmailReviewDialog {
    private readonly activity = inject(ProactiveActivityService);
    private readonly http = inject(HttpClient);
    private readonly toast = inject(ToastService);

    readonly card = input.required<ProactiveActivityItem>();
    readonly sectionLabel = input.required<string>();
    readonly rowIndex = input.required<number>();
    readonly item = input.required<ProactiveItemView>();

    readonly closed = output<void>();

    /** Approval mode (#256): opened from the approvals queue "View" button instead
     *  of the proactive rail. Swaps the proactive Send/Dismiss footer for
     *  Approve / Reject / Approve Always, wired to the queue via these outputs. */
    readonly approvalActions = input<boolean>(false);
    /** Applicable blast-radius rungs (narrow→broad) for the in-modal "Approve
     *  Always" scope chooser. Passed by the host from the approval item's
     *  `scope_rungs`. */
    readonly rungs = input<ScopeRung[]>([]);
    readonly approve = output<void>();
    readonly reject = output<void>();
    /** Emits the chosen scope_tier once the user picks a blast radius and
     *  confirms. The host promotes that cell + approves the item. Replaces the
     *  old void `approveAlways` output, which relied on a background toast/card
     *  chooser that is hidden on the approvals route (item stayed queued). */
    readonly approveAlwaysConfirmed = output<string>();

    /** Reveals the in-modal scope chooser when the user clicks "Approve Always".
     *  Kept local so the chooser lives inside the always-visible modal overlay
     *  instead of a background surface that may be suppressed. */
    readonly scopeChoosing = signal<boolean>(false);

    /** Correction panel state - collapsed until user clicks "Correct". */
    readonly correcting = signal<boolean>(false);
    readonly correctionKind = signal<CorrectionKind>('not_relevant');
    readonly correctionNote = signal<string>('');
    readonly correctionSubmitting = signal<boolean>(false);
    readonly correctionDone = signal<boolean>(false);

    readonly correctionKinds: { value: CorrectionKind; label: string }[] = (
        ['not_relevant', 'wrong_tone', 'inaccurate', 'other'] as CorrectionKind[]
    ).map(k => ({ value: k, label: CORRECTION_LABELS[k] }));

    /** Persona display name with a sane fallback. Used wherever we'd
     *  otherwise say "the bot" - the user wants the assistant referred
     *  to by their hired name (Trey Fix, Sarah Smith, etc). */
    readonly personaName = computed(() => this.card().persona_display_name || 'your DDR');

    /** Editable draft - seeded from item().draft_reply when the dialog
     *  opens. User can revise before clicking Send draft; the edited
     *  text is what actually POSTs to the backend. */
    readonly editedDraft = signal<string>('');
    /** True after user mutates the textarea - used to label the Send
     *  button "Send edited" vs "Send draft" so it's obvious which one
     *  goes out. */
    readonly dirty = computed(() => this.editedDraft() !== (this.item().draft_reply ?? ''));

    /** Already-sent record (a "Sent on your behalf" row), NOT a live draft. When
     *  true the dialog is a read-only receipt: no editable textarea, no Send/
     *  Dismiss, no "edits go out" hint — you cannot un-send or re-send it. */
    readonly isSent = computed(() => this.item().sent === true);

    /** The outbound reply envelope - who it goes to + the reply subject - so the
     *  user sees the ACTUAL message, not just a bare body. Prefer the human NAME
     *  (target_label) over the raw address so a Teams reply reads "To Stefano
     *  Roman", not a bare email or the "the recipient" placeholder. Falls back
     *  to the address, then a channel-appropriate placeholder. */
    readonly replyTo = computed(() => {
        const it = this.item();
        const resolved = (it.target_label || it.from || '').trim();
        if (resolved) {
            return resolved;
        }
        return (it.target_type ?? '').startsWith('teams') ? 'this chat' : 'the recipient';
    });
    /** Cc addresses on the outbound send (#278) - rendered as its own envelope
     *  row so the user sees everyone the email reaches, not just To. Empty
     *  string hides the row (Teams/calendar sends carry no cc). */
    readonly replyCc = computed(() => (this.item().cc ?? []).join(', '));

    /** Only email carries a subject. Teams/calendar sends have none, so the
     *  Subject row renders solely when there's a real subject - hides the
     *  "(no subject)" box that showed on Teams approvals. */
    readonly hasSubject = computed(() => !!(this.item().subject ?? '').trim());
    readonly replySubject = computed(() => {
        const s = (this.item().subject ?? '').trim();
        return /^re:/i.test(s) ? s : `Re: ${s}`;
    });

    // noinspection JSUnusedLocalSymbols
    private readonly _seedDraft = effect(() => {
        this.editedDraft.set(this.item().draft_reply ?? '');
    });

    onBackdrop(evt: MouseEvent): void {
        const target = evt.target as HTMLElement | null;
        if (target?.dataset['backdrop'] === 'true') {
            this.closed.emit();
        }
    }

    /** Chosen a blast radius in the in-modal chooser: hand the scope_tier to the
     *  host (it promotes + approves + closes). */
    onScopeConfirm(scopeTier: string): void {
        this.scopeChoosing.set(false);
        this.approveAlwaysConfirmed.emit(scopeTier);
    }

    onAction(action: ProactiveItemAction, evt: Event): void {
        evt.stopPropagation();
        const c = this.card();
        const sec = this.sectionLabel();
        const idx = this.rowIndex();
        // For Send draft, splice in the (potentially-edited) body so the
        // backend gets what the user actually sees in the textarea. Other
        // actions (Dismiss / Open in Outlook) pass through unchanged.
        let dispatched: ProactiveItemAction = action;
        if (action.kind === 'send_drafted_reply' && action.body) {
            dispatched = {
                ...action,
                body: { ...action.body, body: this.editedDraft() },
            };
        }
        void this.activity.dispatchAction(c.id, sec, idx, dispatched);
        // Send / Dismiss / Open are terminal - close so user sees the
        // updated rail. Buttons themselves render their post-click state
        // ("Sending…" / "✓ done") while open via firingKeys/resolvedKeys.
        if (action.kind === 'send_drafted_reply' || action.kind === 'dismiss_drafted_reply' || action.kind === 'open_web_link') {
            setTimeout(() => this.closed.emit(), 250);
        }
    }

    sendLabel(action: ProactiveItemAction): string {
        if (this.isFiring(action)) {
            return 'Sending…';
        }
        if (this.isResolved(action)) {
            return '✓ done';
        }
        if (action.kind === 'send_drafted_reply' && this.dirty()) {
            return 'Send edited';
        }
        return action.label;
    }

    isFiring(action: ProactiveItemAction): boolean {
        return this.activity.isFiring(this.card().id, this.sectionLabel(), this.rowIndex(), action);
    }

    isResolved(action: ProactiveItemAction): boolean {
        return this.activity.isResolved(this.card().id, this.sectionLabel(), this.rowIndex(), action);
    }

    openCorrectionPanel(): void {
        this.correcting.set(true);
    }

    closeCorrectionPanel(): void {
        this.correcting.set(false);
        this.correctionNote.set('');
    }

    async submitCorrection(): Promise<void> {
        if (this.correctionSubmitting() || this.correctionDone()) {
            return;
        }
        const it = this.item();
        const c = this.card();
        const messageId = it.message_id;
        if (!messageId) {
            void this.toast.error('Cannot record correction: missing message id');
            return;
        }
        this.correctionSubmitting.set(true);
        try {
            await firstValueFrom(
                this.http.post(`${environment.apiBase}/api/ddr/email/correct_draft`, {
                    message_id: messageId,
                    persona_id: c.persona_id,
                    correction_kind: this.correctionKind(),
                    note: this.correctionNote() || null,
                    subject: it.subject,
                    from_addr: it.from,
                    original_draft: it.draft_reply,
                })
            );
            this.correctionDone.set(true);
            void this.toast.success(`Feedback saved - ${this.personaName()} will respect it on future drafts.`);
            // Splice row out of rail so user sees immediate effect.
            this.activity.spliceRow(c.id, this.sectionLabel(), this.rowIndex());
            // Session-wide resolved set - also suppresses the action
            // buttons on the chat system-event card for this message.
            if (messageId) {
                this.activity.markMessageResolved(messageId);
            }
            setTimeout(() => this.closed.emit(), 400);
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'failed';
            void this.toast.error(`Correction failed: ${msg}`);
        } finally {
            this.correctionSubmitting.set(false);
        }
    }
}
